import { useMemo, useState } from "react";
import { Box, Button, InputLabel, MenuItem, TextField } from "@mui/material";
import { BudgetItemTypeEnum } from "../../../../types/budget/budget";
import { BudgetIcons } from "../../../../assets/styled-icons/budget";
import { BudgetItemIcon } from "../components/BudgetItemIcon";
import SupplierAutoComplete from "../../SupplierAutoComplete";
import { sortItemsByCategory } from "../../../../utils/category-sort";
import { useBudgetProvider } from "../../../../contexts/BudgetContext/projectbudgets.context";

type PropTypes = {
  groupName: string;
  /** Categorias habilitadas na forma de cobrança ativa. */
  enabledCategories: Record<string, boolean> | undefined;
  onCreate: (supplierId: string, type: string, supplierName: string) => void;
  onCancel: () => void;
};

/**
 * Altura final do campo de fornecedor — a que ele assume DEPOIS de preenchido,
 * com a razão social na primeira linha e o CNPJ na segunda.
 */
const ALTURA_DO_CAMPO = 46;

/**
 * Trava a altura do controle interno do MUI, seja ele o `Autocomplete` ou o
 * `Select`. Sem isso o autocomplete nasce com 40px e cresce ao receber o
 * fornecedor, e o select fica menor que ele.
 */
const alturaFixa = {
  "& .MuiInputBase-root": {
    height: ALTURA_DO_CAMPO,
    minHeight: ALTURA_DO_CAMPO,
  },
};

/**
 * "Novo fornecedor em <agrupamento>".
 *
 * A linha dentro de um agrupamento é o par (fornecedor, categoria) — por isso o
 * formulário pede os dois. Ela nasce já com um serviço em branco dentro, igual
 * ao que o botão de categoria da visão plana faz.
 *
 * Não é o mesmo fluxo do botão "Novo fornecedor" do rodapé da visão plana, que
 * cria uma linha de fornecedor vazia para o usuário preencher na própria linha.
 */
const NewRowForm: React.FC<PropTypes> = ({
  groupName,
  enabledCategories,
  onCreate,
  onCancel,
}) => {
  const [supplierId, setSupplierId] = useState("");
  const [type, setType] = useState("");
  // O serviço criado junto com a linha guarda o nome do fornecedor, como na
  // visão plana; o autocomplete devolve só o id.
  const provider = useBudgetProvider(supplierId);

  const categoryOptions = useMemo(() => {
    const enabled = Object.keys(enabledCategories ?? {}).filter(
      (key) => enabledCategories?.[key] === true
    );
    return sortItemsByCategory(
      enabled.map((key) => ({
        value: key,
        type: key,
        label: BudgetItemTypeEnum[key as keyof typeof BudgetItemTypeEnum],
      })),
      "type"
    );
  }, [enabledCategories]);

  const canCreate = !!supplierId && !!type;

  return (
    <Box
      sx={{
        border: "1px solid #1361A4",
        borderRadius: "6px",
        backgroundColor: "#FFF",
        p: 2,
        mt: 1,
      }}
    >
      <Box sx={{ fontSize: 15, fontWeight: 600, mb: 1.5, color: "#333" }}>
        Novo fornecedor em{" "}
        <Box component="span" sx={{ color: "#1361A4" }}>
          {groupName}
        </Box>
      </Box>

      {/*
        Larguras proporcionais ao conteúdo; altura FIXA e igual nos dois.
        
        O fornecedor mostra razão social + CNPJ em duas linhas e a categoria
        mostra uma palavra — daí o `flex: 2` / `flex: 1`.
        
        A altura é travada em `ALTURA_DO_CAMPO` porque o autocomplete cresce ao
        receber o fornecedor (40px vazio → 46px preenchido): o campo "pulava" no
        meio do preenchimento e ficava diferente do vizinho. Fixando os dois no
        tamanho final, o formulário não se mexe e as duas caixas ficam iguais.
      */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "flex-start",
        }}
      >
        <Box sx={{ flex: 2, minWidth: 320, ...alturaFixa }}>
          {/*
            Sem rótulo próprio aqui: o `SupplierAutoComplete` já renderiza
            "Fornecedor" por conta dele (`label ?? "Fornecedor"`). Duplicar
            deixava o título repetido em cima do campo.
          */}
          <SupplierAutoComplete
            onSelectSupplier={(value: string) => setSupplierId(value ?? "")}
            provider_id={supplierId}
            title=""
            disabled={false}
            isBudget
            // Escolher aqui um fornecedor que já está no orçamento significa
            // REUSAR a linha dele, não criar uma segunda.
            reuseExistingSupplier
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: 220, ...alturaFixa }}>
          {/*
            "Categoria", como no resto do orçamento: é o rótulo do botão da
            visão plana, dos modais de mover/transferir e do resumo. Este
            formulário era o único a dizer "Tipo de serviço (categoria)".
            
            Mesmo `InputLabel` do autocomplete, para os dois campos casarem.
          */}
          <InputLabel style={{ fontSize: "13px" }}>Categoria</InputLabel>
          <TextField
            select
            fullWidth
            size="small"
            value={type}
            onChange={(event) => setType(event.target.value)}
            inputProps={{ "aria-label": "Categoria" }}
            SelectProps={{
              /*
                O valor selecionado é desenhado aqui, e NÃO reaproveitando o
                `MenuItem`: o ícone da lista tem 40px fixos, que não cabem na
                linha de um campo de 46px — era o que fazia a Categoria esticar
                para 56px e ficar mais alta que o Fornecedor. Na lista o ícone
                continua com o tamanho de sempre.
              */
              renderValue: (value) => {
                const key = value as keyof typeof BudgetItemTypeEnum;
                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <BudgetItemIcon
                      color="#1361A4"
                      icon={BudgetIcons[key]}
                      size={22}
                    />
                    {BudgetItemTypeEnum[key]}
                  </Box>
                );
              },
            }}
          >
            {categoryOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  component="span"
                >
                  <BudgetItemIcon
                    color="#1361A4"
                    icon={BudgetIcons[option.value]}
                  />
                  {option.label}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      {/*
        Os botões ficam na PRÓPRIA linha, alinhados à direita. Antes dividiam a
        linha com o texto explicativo e o "cancelar" ficava cinza-claro, quase
        invisível ao lado do botão azul (apontado no vídeo do QA).
      */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1,
          mt: 2,
        }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={onCancel}
          sx={{ textTransform: "none" }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={!canCreate}
          onClick={() => onCreate(supplierId, type, provider?.name || "")}
          sx={{ textTransform: "none", whiteSpace: "nowrap" }}
        >
          Criar no orçamento
        </Button>
      </Box>
    </Box>
  );
};

export default NewRowForm;
